import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MagicAdLogo } from "@/components/Brand";
import { TreatmentBadge } from "@/components/Badges";
import { Compare } from "@/components/Compare";
import { getToken } from "@/lib/api";
import { setPendingFiles, setPendingUrl } from "@/lib/pending";
import { useSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { ArrowRight, Link2, Upload, Check, ChevronDown, Menu, X, ImagePlus, ShieldCheck, Smartphone, Sparkles, FileText, Download, ScanSearch, ListOrdered } from "lucide-react";

/* ---------------------------------------------------------------------------
   MagicAd by DigiHome - forsiden.
   Designspraak fra digihome.no (PP Right Grotesk, stein-canvas, lilla punktum, haarlinjer, radius 20/12/8), layout bygget
   for et foer/etter-produkt: 100vh-hero der sammenligningen spiller av seg selv rom for rom, deretter én seksjon som viser
   veien fra mobilbilder til ferdig annonse. Alle demoer er laget av MagicAd-motoren (scripts/make_landing_demos.py).
--------------------------------------------------------------------------- */

const LENKER = [["Slik virker det", "#slik-virker-det"], ["Se forskjellen", "#behandlinger"], ["Trygt og ærlig", "#trygt"], ["Spørsmål", "#faq"]];
const IMG = (slug, v) => `/demo/styling/${slug}-${v}.webp`;
const D = (slug) => ({ before: IMG(slug, "before"), after: IMG(slug, "after") });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* the rooms the hero cycles through - one per treatment, styling first because that is the "wow" */
const DEMOS = [
  { id: "soverom", label: "Soverom", kind: "soft_style", before: IMG("soverom", "before"), after: IMG("soverom", "after"), beforeLabel: "Ekte bilde", did: ["Sengen redd på nytt", "Nattbordet ryddet", "Lampen på", "Klær og kabler borte"] },
  { id: "stue", label: "Stue", kind: "soft_style", before: IMG("stue", "before"), after: IMG("stue", "after"), beforeLabel: "Ekte bilde", did: ["Sofaen redd opp", "Bordet ryddet", "Kabler borte", "Riktig lys – samme møbler, samme plass"] },
  { id: "bad", label: "Bad", kind: "soft_style", before: IMG("bad", "before"), after: IMG("bad", "after"), beforeLabel: "Ekte bilde", did: ["Gulstikk og mørke borte", "Toalettsaker ryddet", "Rene håndklær og såpe", "Samme fliser, samme dusj"] },
  { id: "tomt-soverom", label: "Tomt rom", kind: "virtual_stage", before: IMG("tomt-soverom", "before"), after: IMG("tomt-soverom", "after"), beforeLabel: "Ekte bilde – tomt", did: ["Seng mot den frie veggen", "Ett nattbord, én lampe", "Skapet kan åpnes", "Merket «Virtuelt møblert»"] },
];
/* the headline's last words - each a promise the pictures keep */
const ORD = ["leier ut", "selger", "får klikk", "overbeviser"];
const WORD_MS = 3400;
/* timing: the first room starts as the stage has just risen in, later rooms get a short beat on the real photo after the
   crossfade; the sweep is a 4.6 s glide - a soft ramp, a calm cruise, a long landing - never a rush, never a dead start */
const ROOM_MS = { holdFirst: 560, hold: 820, sweep: 4600, land: 2000, fade: 700 };
const roomTotal = (first) => (first ? ROOM_MS.holdFirst : ROOM_MS.hold) + ROOM_MS.sweep + ROOM_MS.land;
const KIND_NB = { optimize_only: "Optimalisert", soft_style: "Digitalt stylet", virtual_stage: "Virtuelt møblert" };

const TALL = [["1–2", "min", "fra opplasting til ferdig galleri"], ["2", "kontroller", "på hvert eneste bilde"], ["3", "behandlinger", "optimalisert · stylet · møblert"], ["0", "endringer", "på boligen – aldri"]];

const HOW = [
  { Icon: Smartphone, t: "Start med det du har", d: "Ta bildene med mobilen, eller lim inn FINN- eller Airbnb-annonsen du allerede har. Ingen fotograf, ingen oppsett.", ms: 5600 },
  { Icon: Sparkles, t: "DigiHome tar fagvalgene", d: "Hvert bilde får riktig behandling – optimalisert, digitalt stylet eller virtuelt møblert – og kontrolleres mot originalen. Beste ekte bilde først.", ms: 8600 },
  { Icon: FileText, t: "Ferdig annonse på minutter", d: "Bildepakke, hovedbilde, rekkefølge og et annonseutkast som ikke lover mer enn bildene viser. Lim inn i FINN eller Airbnb. Publiser.", ms: 9000 },
];
/* the engine's calls for the example home - what step 2 shows */
const FAGVALG = [
  { slug: "soverom", room: "Soverom", kind: "soft_style", why: "Møblert, uryddig seng" },
  { slug: "stue", room: "Stue", kind: "soft_style", why: "Rot på bordet, kabler" },
  { slug: "tomt-soverom", room: "Soverom 2", kind: "virtual_stage", why: "Tomt rom" },
  { slug: "kjokken", room: "Kjøkken", kind: "soft_style", why: "Full benk" },
  { slug: "bad", room: "Bad", kind: "soft_style", why: "Gulstikk, toalettsaker" },
];

const BEHANDLINGER = [
  { kind: "optimize_only", slug: "stue-lys", before: IMG("stue-lys", "before"), after: IMG("stue-lys", "after"), nr: "01", title: "Optimalisert", text: "Rette vertikaler, riktig lys og hvitbalanse, ærlig utsnitt. Ikke generativt – boligen er uendret, og hvert bilde kontrolleres pikselvis.", punkter: ["Standard for alle bilder", "Rette linjer, ærlig lys", "Beste bilde først"] },
  { kind: "soft_style", slug: "soverom", before: IMG("soverom", "before"), after: IMG("soverom", "after"), nr: "02", title: "Digitalt stylet", text: "Samme rom, samme møbler på samme plass. Sengen redd på nytt, puter og tekstiler, ryddige flater, lampene på – slik en boligstylist ville gjort det på tjue minutter.", punkter: ["Synlig forskjell, samme rom", "Merket «Digitalt stylet»", "Alltid etter det ekte bildet"] },
  { kind: "virtual_stage", slug: "tomt-soverom", before: IMG("tomt-soverom", "before"), after: IMG("tomt-soverom", "after"), nr: "03", title: "Virtuelt møblert", text: "Tomme rom møbleres slik en leietaker faktisk kan bo der: riktig sengestørrelse, fri gangsone, skap som kan åpnes. Ingen hotellsymmetri.", punkter: ["Kontroll: kan man bo her?", "Merket «Virtuelt møblert»", "Aldri hovedbilde"] },
];

const TRYGT = ["Ekte, optimaliserte bilder er alltid standard og alltid først", "Visualiseringer merkes i selve bildet og blir aldri hovedbilde", "Vegger, gulv, vinduer, tilstand og romgeometri beholdes – aldri skjult", "Annonseteksten påstår aldri noe bildene ikke viser", "Møbler på bildene omtales ikke som inkludert uten at du sier det"];

const FAQ = [
  ["Er dette lov i boligannonser?", "Ja, når det gjøres ærlig. Standardbildene er ekte og bare optimalisert. Digitalt stylede og virtuelt møblerte bilder er visualiseringer: de merkes tydelig i bildet, kommer alltid etter det ekte bildet av samme rom og blir aldri hovedbilde."],
  ["Endrer dere boligen?", "Nei. Vegger, gulv, vinduer, dører, fast innredning, tilstand og romgeometri er uendret. Hvert bilde kontrolleres mot originalen med både bildeanalyse og en uavhengig modell før det anbefales."],
  ["Hvor mange valg må jeg ta?", "To. Utleietype og hva som følger med boligen – pluss stylingnivå (Ingen / Anbefalt / Mer innbydende). Sengetøy, puter, lamper og hva som ryddes er DigiHomes fagvurdering."],
  ["Kan jeg bruke egne bilder uten annonse?", "Ja. Last opp bildene rett fra telefonen eller datamaskinen – på forsiden eller inne i appen. Du trenger ingen FINN- eller Airbnb-annonse."],
  ["Er eksemplene på denne siden ekte?", "«Før»-bildene er eksempelrom laget for å ligne typiske annonsebilder. «Etter»-bildene er laget av MagicAd-motoren på nøyaktig samme måte som for dine bilder – og godkjent av den samme kontrollen. På dine egne bilder ser du alltid før og etter selv."],
  ["Hva skjer med originalene?", "De lagres uendret og privat, og ligger alltid med i bildepakken. Alle lenker til bildene er tidsbegrensede og personlige."],
];

/* the action: one pill - link field, upload, go */
const Handling = ({ onUrl, onFiles, dark = false, testid = "landing" }) => {
  const [url, setUrl] = useState("");
  const fileRef = useRef(null);
  const go = () => { if (url.trim()) onUrl(url.trim()); else fileRef.current?.click(); };
  return (
    <div className={cn("w-full rounded-[16px] p-1.5 flex flex-col sm:flex-row sm:items-center gap-1.5", dark ? "bg-white/[0.08] ring-1 ring-white/15 backdrop-blur-md" : "bg-card ring-hair card-shadow")} data-testid={`${testid}-handling`}>
      <div className="relative flex-1 min-w-0">
        <Link2 className={cn("h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2", dark ? "text-white/50" : "text-foreground/40")} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Lim inn FINN- eller Airbnb-lenke" aria-label="Annonselenke"
          className={cn("h-12 w-full bg-transparent pl-11 pr-3 text-[15.5px] focus:outline-none", dark ? "text-white placeholder:text-white/45" : "text-foreground placeholder:text-foreground/45")} data-testid={`${testid}-url-input`} />
      </div>
      <div className={cn("hidden sm:block h-7 w-px", dark ? "bg-white/15" : "bg-foreground/10")} />
      <div className="flex items-center gap-1.5">
        <Button variant={dark ? "light" : "ghost"} onClick={() => fileRef.current?.click()} className={cn("flex-1 sm:flex-none", !dark && "text-foreground/80")} data-testid={`${testid}-upload-button`}><Upload className="h-4 w-4" />Last opp bilder</Button>
        <Button onClick={go} aria-label="Fortsett" className="w-12 px-0" data-testid={`${testid}-start`}><ArrowRight className="h-5 w-5" /></Button>
      </div>
      <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-file-input`} />
    </div>
  );
};

/* hero stage: each room plays as a small film - the real photo, a calm full reveal to the result, a beat to let it land,
   then a crossfade into the next room. Pure autoplay: the line is a reveal, not a control (the room dots still let a
   visitor jump to a room). */
const Stage = ({ index, onIndex }) => {
  const demo = DEMOS[index];
  const cmp = useRef(null);
  const first = useRef(true);                    // the opening room plays with the shorter hold
  const [ghost, setGhost] = useState(null);      // previous room's result, fading out over the new room
  const [run, setRun] = useState(0);             // bumps to replay the current room
  const [barMs, setBarMs] = useState(roomTotal(true));

  useEffect(() => {
    // warm the other rooms right away (the first room's two images are already eager in the DOM)
    const t = setTimeout(() => DEMOS.slice(1).forEach((d) => [d.before, d.after].forEach((src) => { const i = new Image(); i.decoding = "async"; i.src = src; })), 300);
    return () => clearTimeout(t);
  }, []);

  // the sequence - keyed on the room and on `run` so a dot press can replay the current room
  useEffect(() => {
    let cancelled = false;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const opening = first.current;
    first.current = false;
    const play = async () => {
      const c = cmp.current;
      if (!c) return;
      const hold = opening ? ROOM_MS.holdFirst : ROOM_MS.hold;
      setBarMs(hold + ROOM_MS.sweep + ROOM_MS.land);
      c.jump(100);                                          // the real photo
      await sleep(hold);
      if (cancelled) return;
      if (reduced) { c.jump(45); return; }
      const ok = await c.animate(0, ROOM_MS.sweep, "glide"); // the whole way, before -> after
      if (!ok || cancelled) return;
      await sleep(ROOM_MS.land);                            // let the result land
      if (cancelled) return;
      setGhost(demo.after);                                 // crossfade: the result stays while the next room's real photo comes in
      onIndex((index + 1) % DEMOS.length);
    };
    play();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, run]);

  const go = (k) => { setGhost(DEMOS[index].after); if (k === index) setRun((r) => r + 1); else onIndex(k); };

  return (
    <div className="space-y-3 sm:space-y-4" data-testid="landing-stage">
      <div className="relative">
        <Compare key={demo.id} ref={cmp} before={demo.before} after={demo.after} kind={demo.kind} beforeLabel={demo.beforeLabel} initial={100} priority interactive={false} aspect="4 / 3" rounded="rounded-[20px] sm:rounded-[24px]" className="card-shadow" testid="landing-scene">
          <span className="absolute bottom-3 left-3 rounded-[8px] bg-black/55 text-white px-2.5 py-1 text-[12px] font-medium backdrop-blur pointer-events-none" data-testid="landing-stage-room">{demo.label} · {KIND_NB[demo.kind]}</span>
        </Compare>
        {ghost && (
          <img key={ghost + index} src={ghost} alt="" aria-hidden="true" onAnimationEnd={() => setGhost(null)}
            className="ghost-out pointer-events-none absolute inset-0 h-full w-full object-cover rounded-[20px] sm:rounded-[24px]" />
        )}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Rom" data-testid="landing-stage-dots">
          {DEMOS.map((d, k) => (
            <button key={d.id} type="button" role="tab" aria-selected={k === index} aria-label={`${d.label} – ${KIND_NB[d.kind]}`} onClick={() => go(k)}
              className={cn("relative h-2 overflow-hidden rounded-full transition-[width,background-color] duration-500", k === index ? "w-9 bg-foreground/15" : "w-2 bg-foreground/20 hover:bg-foreground/40")} data-testid={`landing-stage-dot-${d.id}`}>
              {k === index && <span key={`${d.id}-${run}`} className="bar-fill absolute inset-0 rounded-full bg-foreground" style={{ "--bar-ms": `${barMs}ms` }} />}
            </button>
          ))}
          <span className="ml-2 text-[12.5px] text-svak">Ekte bilde → resultat</span>
        </div>
        <span className="text-[12.5px] text-svak">Eksempelrom · laget av MagicAd-motoren</span>
      </div>
      <ul key={demo.id} className="fade-swap flex flex-wrap gap-x-4 gap-y-1.5 text-[13.5px] font-medium text-foreground/80" data-testid="landing-stage-did">
        {demo.did.map((t) => <li key={t} className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[hsl(var(--accent-strong))]" strokeWidth={2.2} />{t}</li>)}
      </ul>
    </div>
  );
};

/* rotating headline: "Bilder som" stays, the promise underneath changes - the old word lifts out, the new rises in */
const Headline = () => {
  const [i, setI] = useState(0);
  const [prev, setPrev] = useState(null);
  const last = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setI((k) => (k + 1) % ORD.length), WORD_MS);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (i !== last.current) { setPrev(last.current); last.current = i; } }, [i]);
  return (
    <h1 className="display rise-in rise-in-1 mt-5 text-[58px] sm:text-[84px] lg:text-[clamp(64px,5.6vw,104px)]" data-testid="landing-h1">
      Bilder som<br />
      <span className="word-slot relative inline-block align-top overflow-hidden pt-[0.12em] -mt-[0.12em] pb-[0.3em] -mb-[0.3em] pr-[0.04em]" aria-live="polite" aria-atomic="true">
        <span key={i} className="word-in inline-block whitespace-nowrap" data-testid="landing-h1-word">{ORD[i]}<span className="dot">.</span></span>
        {prev !== null && (
          <span key={`out-${prev}-${i}`} aria-hidden="true" onAnimationEnd={() => setPrev(null)} className="word-out absolute left-0 top-[0.12em] inline-block whitespace-nowrap">{ORD[prev]}<span className="dot">.</span></span>
        )}
      </span>
    </h1>
  );
};

/* section 2: three acts that play by themselves while in view - (1) the phone photos arrive, (2) the engine makes its calls
   with a live before/after, (3) the ad builds itself. Click a step to jump; the bar under the active step shows the time. */
const THUMBS = [
  ["stue-lys", "after", "optimize_only"], ["soverom", "after", "soft_style"], ["kjokken", "after", "soft_style"],
  ["tomt-soverom", "after", "virtual_stage"], ["bad", "after", "soft_style"], ["stue", "after", "soft_style"],
];
const dl = (sec) => ({ animationDelay: `${sec}s` });

/* the five phone photos of the example home, in the order they were taken */
const RULL = [["soverom", "Soverom"], ["stue", "Stue"], ["kjokken", "Kjøkken"], ["bad", "Bad"], ["tomt-soverom", "Soverom 2"]];

const ActPhotos = () => (
  <div className="ad-anim play flex h-full flex-col p-5 sm:p-6 lg:p-7" data-testid="landing-how-act-photos">
    <div data-ad="in" style={dl(0.05)} className="flex items-center justify-between gap-3 text-[13px] text-dim">
      <span className="inline-flex items-center gap-2"><Smartphone className="h-4 w-4" />Kamerarull · i dag</span>
      <span className="rounded-[8px] bg-secondary px-2 py-0.5 font-medium text-foreground/70">5 bilder · HEIC</span>
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {RULL.map(([sl, room], i) => (
        <div key={sl} data-ad="pop" style={dl(0.2 + i * 0.16)} className="relative aspect-[3/2] overflow-hidden rounded-[12px] bg-[hsl(var(--tint))] ring-1 ring-black/5">
          <img src={IMG(sl, "before")} alt={`${room} – slik bildet ble tatt`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
          <span data-ad="scan" style={dl(1.3 + i * 0.16)} className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-transparent via-[hsl(var(--accent))]/45 to-transparent" aria-hidden="true" />
          <span className="absolute bottom-2 left-2 rounded-[6px] bg-black/55 text-white px-1.5 py-0.5 text-[10.5px] font-medium backdrop-blur">IMG_{2041 + i}.HEIC</span>
          <span data-ad="pop" style={dl(1.7 + i * 0.16)} className="absolute top-2 right-2 rounded-[6px] bg-white/90 text-[hsl(var(--ink))] px-1.5 py-0.5 text-[10.5px] font-medium">{room}</span>
        </div>
      ))}
      <div data-ad="pop" style={dl(1.05)} className="relative flex aspect-[3/2] flex-col justify-between rounded-[12px] bg-foreground p-4 text-background">
        <div>
          <div className="text-[13px] text-background/70">…eller lim inn annonsen du allerede har</div>
          <div className="mt-1 hidden text-[12.5px] text-background/50 sm:block">Vi henter bildene og nøkkelfakta.</div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 rounded-[8px] bg-white/10 px-2.5 py-1.5 text-[12px] font-mono"><Link2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent))]" /><span className="truncate">finn.no/realestate/lettings/…</span></div>
          <div className="flex items-center gap-2 rounded-[8px] bg-white/10 px-2.5 py-1.5 text-[12px] font-mono"><Link2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent))]" /><span className="truncate">airbnb.no/rooms/…</span></div>
        </div>
      </div>
    </div>
    <div data-ad="in" style={dl(2.4)} className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5 text-[13.5px]">
      <span className="inline-flex items-center gap-2 text-foreground/80"><Upload className="h-4 w-4 text-[hsl(var(--accent-strong))]" />Slik bildene er – det er nok. Ingen fotograf, ingen oppsett.</span>
      <span className="text-svak text-[12.5px]">Originalene lagres uendret</span>
    </div>
  </div>
);

const ActEngine = () => (
  <div className="ad-anim play grid h-full gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:p-7" data-testid="landing-how-act-engine">
    <div data-ad="in" style={dl(0.05)} className="min-w-0 self-center">
      <Compare {...D("soverom")} kind="soft_style" beforeLabel="Ekte bilde" sweep initial={30} priority rounded="rounded-[16px]" testid="landing-how-compare">
        <span className="absolute bottom-3 left-3 rounded-[8px] bg-black/55 text-white px-2.5 py-1 text-[12px] font-medium backdrop-blur pointer-events-none">Soverom · dra i linjen</span>
      </Compare>
      <div className="mt-3 flex flex-wrap gap-1.5 text-[13px]">
        {["Samme seng, samme plass", "Redd på nytt", "Nattbordet ryddet", "Lampen på", "Kontrollert mot originalen"].map((t, i) => (
          <span key={t} data-ad="pop" style={dl(1.6 + i * 0.14)} className="inline-flex items-center gap-1.5 rounded-[8px] bg-secondary px-2.5 py-1 font-medium text-foreground/80"><Check className="h-3.5 w-3.5 text-[hsl(var(--accent-strong))]" strokeWidth={2.2} />{t}</span>
        ))}
      </div>
    </div>
    <div className="flex min-w-0 flex-col" data-testid="landing-how-calls">
      <div data-ad="in" style={dl(0.3)} className="flex items-center justify-between gap-2 text-[13px] text-dim">
        <span className="inline-flex items-center gap-2"><ScanSearch className="h-4 w-4" />Fagvalg per bilde</span>
        <span className="inline-flex items-center gap-1.5"><span className="flex gap-1">{[0, 1, 2].map((k) => <span key={k} className="dot-run h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent-strong))]" style={{ animationDelay: `${k * 0.2}s` }} />)}</span>analyserer</span>
      </div>
      <ul className="mt-3 divide-y hair border-y hair">
        {FAGVALG.map((f, i) => (
          <li key={f.slug} data-ad="in" style={dl(0.7 + i * 0.42)} className="flex items-center gap-3 py-2.5">
            <span className="h-11 w-14 shrink-0 overflow-hidden rounded-[8px] bg-[hsl(var(--tint))]"><img src={IMG(f.slug, "before")} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" /></span>
            <span className="min-w-0 flex-1"><span className="block text-[14.5px] font-medium leading-tight">{f.room}</span><span className="block truncate text-[12.5px] text-dim">{f.why}</span></span>
            <span data-ad="pop" style={dl(0.95 + i * 0.42)} className="shrink-0"><TreatmentBadge kind={f.kind} /></span>
          </li>
        ))}
      </ul>
      <div data-ad="in" style={dl(3.1)} className="mt-auto space-y-2 pt-4 text-[13.5px] text-foreground/80">
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-[hsl(var(--accent-strong))]" />Hvert bilde kontrollert mot originalen – to ganger</div>
        <div className="flex items-center gap-2"><ListOrdered className="h-4 w-4 shrink-0 text-[hsl(var(--accent-strong))]" />Hovedbilde: stuen, ekte og optimalisert – visualiseringer kommer etter</div>
      </div>
    </div>
  </div>
);

const ActAd = () => (
  <div className="ad-anim play flex h-full flex-col" data-testid="landing-ad-mock">
    <div className="flex items-center justify-between gap-3 border-b hair px-5 py-3 text-[13px] sm:px-6 lg:px-7">
      <span data-ad="in" style={dl(0.05)} className="inline-flex items-center gap-2 font-medium text-foreground/70"><FileText className="h-4 w-4" />Annonseutkast</span>
      <span data-ad="pop" style={dl(4.4)} className="inline-flex items-center gap-1.5 rounded-[8px] bg-[hsl(var(--accent))] px-2 py-0.5 font-medium text-[hsl(var(--ink))]"><Check className="h-3.5 w-3.5" strokeWidth={2.2} />Klar til publisering</span>
    </div>
    <div className="grid flex-1 gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:p-7">
      <div className="min-w-0">
        <div className="relative aspect-[16/10] overflow-hidden rounded-[16px] bg-[hsl(var(--tint))]">
          <div data-ad="wipe" style={dl(0.2)} className="absolute inset-0">
            <img src={IMG("stue-lys", "after")} alt="Stue – optimalisert hovedbilde" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
            <span className="absolute top-3 left-3"><TreatmentBadge kind="optimize_only" /></span>
            <span className="absolute top-3 right-3 rounded-[8px] bg-black/55 px-2 py-1 text-[11.5px] font-medium text-white backdrop-blur">Hovedbilde · 1/7</span>
          </div>
        </div>
        <div className="mt-2.5 grid grid-cols-6 gap-2">
          {THUMBS.map(([sl, v, kind], i) => (
            <div key={`${sl}-${v}`} data-ad="pop" style={dl(1.0 + i * 0.15)} className="relative aspect-[4/3] overflow-hidden rounded-[8px] bg-[hsl(var(--tint))]">
              <img src={IMG(sl, v)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
              <span className={cn("absolute bottom-1 left-1 rounded-[4px] px-1 py-px text-[8.5px] font-medium leading-tight sm:text-[9.5px]", kind === "optimize_only" ? "bg-white/90 text-[hsl(var(--ink))]" : "badge-" + (kind === "soft_style" ? "style" : "furn"))}>{KIND_NB[kind]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex min-w-0 flex-col">
        <h3 data-ad="line" style={dl(2.0)} className="font-heading text-[24px] leading-[1.05] tracking-[-0.02em] sm:text-[30px]">Lys 2-roms med god planløsning og stille soverom</h3>
        <div data-ad="line" style={dl(2.35)} className="mt-2 text-[13.5px] text-dim">Leilighet · 62 m² · 2 soverom · Umøblert · Langtidsleie</div>
        <div className="mt-4 space-y-2 text-[14.5px] leading-[1.55] text-foreground/85">
          <p data-ad="line" style={dl(2.7)}>Lys og praktisk leilighet med romslig stue, soverom med skyvedørsgarderobe og kjøkken med god benkeplass.</p>
          <p data-ad="line" style={dl(3.05)}>Parkett på gulvene, bad med dusj. Rolig beliggenhet med kort vei til kollektiv.</p>
          <p data-ad="line" style={dl(3.4)} className="text-[13px] text-dim">Bilder merket «Digitalt stylet» og «Virtuelt møblert» er visualiseringer – møbler følger ikke med.</p>
        </div>
        <div className="mt-auto flex flex-wrap gap-1.5 pt-5">
          {[["Bildepakke · 7 bilder", Download, 3.8], ["Annonsetekst kopiert", FileText, 4.0], ["FINN og Airbnb", Check, 4.2]].map(([t, Icon, d]) => (
            <span key={t} data-ad="pop" style={dl(d)} className="inline-flex items-center gap-1.5 rounded-[8px] bg-secondary px-2.5 py-1 text-[12.5px] font-medium"><Icon className="h-3.5 w-3.5" />{t}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ACTS = [ActPhotos, ActEngine, ActAd];

const HowItWorks = ({ loggedIn }) => {
  const ref = useRef(null);
  const [step, setStep] = useState(0);
  const [on, setOn] = useState(false);
  const [cycle, setCycle] = useState(0);        // bumps when the same step is re-entered so its act replays
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((es) => setOn(es.some((e) => e.isIntersecting)), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!on) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setTimeout(() => setStep((k) => (k + 1) % HOW.length), HOW[step].ms);
    return () => clearTimeout(t);
  }, [on, step, cycle]);
  const pick = (k) => { if (k === step) setCycle((c) => c + 1); else setStep(k); };
  const Act = ACTS[step];
  return (
    <div ref={ref} className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-14 xl:gap-20 lg:items-start" data-testid="landing-how-grid">
      <div className="lg:sticky lg:top-[104px]">
        <div className="eyebrow">Slik virker det</div>
        <h2 className="display mt-3 text-[42px] sm:text-[56px] lg:text-[64px]">Fra mobilbilder til ferdig annonse<span className="dot">.</span></h2>
        <p className="mt-5 max-w-[46ch] text-[17px] leading-[1.5] text-dim">Ta bildene med mobilen – eller bruk annonsen du allerede har. MagicAd gjør resten: optimaliserer, styler, sorterer galleriet, skriver et ærlig utkast og pakker alt klart for FINN eller Airbnb.</p>
        <ol className="mt-8 border-t hair" role="tablist" aria-label="Steg">
          {HOW.map((s, i) => {
            const active = i === step;
            return (
              <li key={s.t} className="border-b hair">
                <button type="button" role="tab" aria-selected={active} onClick={() => pick(i)} className={cn("relative grid w-full grid-cols-[44px_minmax(0,1fr)] gap-x-4 py-5 text-left rounded-[8px] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", !active && "opacity-55 hover:opacity-90")} data-testid={`landing-step-${i + 1}`}>
                  <span className={cn("flex h-11 w-11 items-center justify-center rounded-[12px] transition-colors", active ? "bg-foreground text-background" : "bg-card ring-hair")}><s.Icon className="h-[18px] w-[18px]" strokeWidth={1.7} /></span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2.5 pt-1"><span className="font-heading text-[13px] tabular-nums text-foreground/45">0{i + 1}</span><span className="text-[17px] font-medium leading-tight">{s.t}</span></span>
                    {active && <span className="fade-swap mt-1.5 block text-[14.5px] leading-[1.5] text-dim">{s.d}</span>}
                    <span className={cn("mt-3 block h-[2px] w-full overflow-hidden rounded-full", active ? "bg-foreground/10" : "bg-transparent")}>
                      {active && on && <span key={`${step}-${cycle}`} className="bar-fill block h-full rounded-full bg-foreground" style={{ "--bar-ms": `${s.ms}ms` }} />}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <Button className="mt-8" asChild><Link to={loggedIn ? "/app" : "/registrer"} data-testid="landing-how-cta">Prøv med dine bilder<ArrowRight className="h-4 w-4" /></Link></Button>
      </div>
      {/* one frame, three acts - the frame never changes size, so the page never jumps */}
      <div className="relative min-w-0 overflow-hidden rounded-[24px] bg-card ring-hair card-shadow lg:h-[620px]" data-testid="landing-how-visual" data-step={step}>
        <div className="pointer-events-none absolute inset-0 lilla-glow" aria-hidden="true" />
        <div key={`${step}-${cycle}`} className="step-in relative h-full"><Act /></div>
      </div>
    </div>
  );
};

export default function LandingPage() {
  const nav = useNavigate();
  const [open, setOpen] = useState(0);
  const [meny, setMeny] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [index, setIndex] = useState(0);
  const heroRef = useRef(null);
  const loggedIn = Boolean(getToken());
  useSeo({ title: "Bilder som leier ut", description: "Fra mobilbilder til ferdig boligannonse på minutter. MagicAd by DigiHome optimaliserer, styler og møblerer bildene som en profesjonell boligstylist – alltid ærlig, alltid merket." });
  useEffect(() => {
    let raf = 0;
    const f = () => { raf = 0; setScrolled(window.scrollY > 8); };
    const on = () => { if (!raf) raf = window.requestAnimationFrame(f); };
    window.addEventListener("scroll", on, { passive: true });
    return () => { window.removeEventListener("scroll", on); if (raf) window.cancelAnimationFrame(raf); };
  }, []);

  const next = loggedIn ? "/app" : "/registrer?next=%2Fapp";
  const startUrl = useCallback((u) => { setPendingUrl(u); nav(next); }, [nav, next]);
  const startFiles = useCallback((list) => {
    const n = setPendingFiles(list);
    if (!n) { toast.error("Velg bildefiler (JPEG, PNG, WEBP eller HEIC)"); return; }
    toast.success(`${n} bilde${n === 1 ? "" : "r"} klare`, { description: loggedIn ? "Vi går videre til ny annonse." : "Opprett en gratis konto, så fortsetter vi der." });
    nav(next);
  }, [nav, next, loggedIn]);

  // the whole page takes drag-and-drop
  useEffect(() => {
    let depth = 0;
    const has = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const enter = (e) => { if (!has(e)) return; e.preventDefault(); depth += 1; setDropping(true); };
    const over = (e) => { if (!has(e)) return; e.preventDefault(); };
    const leave = (e) => { if (!has(e)) return; depth = Math.max(0, depth - 1); if (depth === 0) setDropping(false); };
    const drop = (e) => { if (!has(e)) return; e.preventDefault(); depth = 0; setDropping(false); startFiles(e.dataTransfer.files); };
    window.addEventListener("dragenter", enter); window.addEventListener("dragover", over); window.addEventListener("dragleave", leave); window.addEventListener("drop", drop);
    return () => { window.removeEventListener("dragenter", enter); window.removeEventListener("dragover", over); window.removeEventListener("dragleave", leave); window.removeEventListener("drop", drop); };
  }, [startFiles]);

  const lenke = "inline-flex h-9 items-center rounded-[9px] px-3 text-[14.5px] text-foreground/70 transition-colors hover:bg-foreground/[0.05] hover:text-foreground";
  const wide = "w-full px-5 sm:px-8 lg:px-12 2xl:px-16";   // full-bleed rhythm shared by nav and hero

  return (
    <div className="min-h-screen overflow-x-clip bg-background text-foreground antialiased" data-testid="landing-page">
      {dropping && (
        <div className="fixed inset-0 z-[60] bg-background/85 backdrop-blur-md flex items-center justify-center p-6 pointer-events-none" data-testid="landing-drop-overlay">
          <div className="rounded-[24px] border-2 border-dashed border-[hsl(var(--accent))] bg-card/80 px-10 py-12 text-center">
            <ImagePlus className="h-8 w-8 mx-auto text-[hsl(var(--accent-strong))]" />
            <div className="display-sm mt-4 text-[32px]">Slipp bildene her<span className="dot">.</span></div>
            <p className="mt-2 text-dim">JPEG, PNG, WEBP eller HEIC · opptil 25 bilder</p>
          </div>
        </div>
      )}

      {/* nav - full width */}
      <header className={cn("sticky top-0 z-50 border-b bg-background/85 backdrop-blur-md transition-colors duration-300", scrolled || meny ? "hair" : "border-transparent")} data-testid="landing-nav">
        <div className={cn(wide, "relative flex h-[68px] items-center justify-between gap-4 lg:h-[72px]")}>
          <div className="flex min-w-0 items-center gap-10">
            <Link to="/" className="flex shrink-0 items-center rounded-md" aria-label="MagicAd by DigiHome" onClick={() => setMeny(false)}><MagicAdLogo size="lg" /></Link>
            <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Hovedmeny">{LENKER.map(([l, h]) => <a key={l} href={h} className={lenke}>{l}</a>)}</nav>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {loggedIn ? <Button size="sm" onClick={() => nav("/app")} data-testid="landing-open-app">Til appen<ArrowRight className="h-4 w-4" /></Button> : (
              <>
                <Link to="/logg-inn" className={cn(lenke, "hidden sm:inline-flex")} data-testid="landing-login">Logg inn</Link>
                <Button size="sm" asChild className="hidden sm:inline-flex"><Link to="/registrer" data-testid="landing-register">Kom i gang</Link></Button>
              </>
            )}
            <button type="button" onClick={() => setMeny((v) => !v)} aria-expanded={meny} aria-label={meny ? "Lukk meny" : "Åpne meny"} className="lg:hidden -mr-2 flex h-11 w-11 items-center justify-center rounded-full hover:bg-foreground/[0.05]" data-testid="landing-menu-button">
              {meny ? <X className="h-5 w-5" strokeWidth={1.6} /> : <Menu className="h-5 w-5" strokeWidth={1.6} />}
            </button>
          </div>
        </div>
        {meny && (
          <div className="lg:hidden border-t hair bg-background px-5 pb-6 pt-2" data-testid="landing-mobile-menu">
            <nav className="flex flex-col">{LENKER.map(([l, h]) => <a key={l} href={h} onClick={() => setMeny(false)} className="border-b hair py-4 font-heading text-[26px]">{l}</a>)}</nav>
            <div className="mt-6 flex flex-col gap-2">
              <Button asChild><Link to="/registrer" data-testid="landing-register-mobile">Kom i gang</Link></Button>
              <Button variant="outline" asChild><Link to="/logg-inn" data-testid="landing-login-mobile">Logg inn</Link></Button>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* hero: full viewport - promise left, proof right (stacked on mobile) */}
        <section ref={heroRef} className="relative scroll-mt-[72px] min-h-[calc(100svh-68px)] lg:min-h-[calc(100svh-72px)] flex" data-testid="landing-hero">
          <div className={cn(wide, "grid gap-8 py-8 sm:py-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-14 lg:py-10 xl:gap-20 self-center")}>
            <div className="max-w-[640px]">
              <div className="rise-in inline-flex items-center gap-2 rounded-[8px] bg-card ring-hair px-2.5 py-1 text-[12.5px] font-medium text-foreground/75"><Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent-strong))]" />Boligstylist på autopilot – for utleiere</div>
              <Headline />
              <p className="rise-in rise-in-1 mt-5 max-w-[46ch] text-[17px] leading-[1.45] text-dim sm:text-[20px]" data-testid="landing-ingress">
                Lim inn annonsen eller last opp bildene fra mobilen. MagicAd optimaliserer, styler og møblerer – som en profesjonell boligstylist. Alltid ærlig, alltid merket.
              </p>
              <div className="rise-in rise-in-2 relative z-20 mt-7 w-full max-w-[600px]">
                <Handling onUrl={startUrl} onFiles={startFiles} />
                <p className="mt-3 text-[13px] text-svak">Eller slipp bildene hvor som helst på siden. Gratis å prøve · originalene lagres uendret.</p>
              </div>
            </div>
            <div className="rise-in rise-in-2 min-w-0 lg:justify-self-end w-full" style={{ maxWidth: "min(100%, calc((100svh - 250px) * 4 / 3))" }}>
              <Stage index={index} onIndex={setIndex} />
            </div>
          </div>
        </section>

        {/* proof numbers */}
        <section aria-label="Nøkkeltall" className="border-y hair" data-testid="landing-numbers">
          <div className={cn(wide, "grid grid-cols-2 gap-y-8 py-10 lg:grid-cols-4 lg:py-12")}>
            {TALL.map(([n, unit, txt], i) => (
              <div key={unit} className={cn(i > 0 && "lg:border-l hair lg:pl-8", i % 2 === 1 && "border-l hair pl-6 lg:pl-8")}>
                <div className="font-heading text-[44px] leading-none tracking-[-0.03em] sm:text-[56px]">{n}<span className="ml-2 text-[18px] tracking-normal text-dim sm:text-[22px]">{unit}</span></div>
                <div className="mt-2 text-[14px] text-dim">{txt}</div>
              </div>
            ))}
          </div>
        </section>

        {/* section 2: from phone photos to a finished ad - three acts that play themselves */}
        <section id="slik-virker-det" className={cn(wide, "py-16 lg:py-28 scroll-mt-[72px]")} data-testid="landing-how">
          <HowItWorks loggedIn={loggedIn} />
        </section>

        {/* three treatments - each card sends you back to the live stage */}
        <section id="behandlinger" className="relative overflow-hidden bg-[hsl(var(--plum))] text-[hsl(var(--offwhite))] scroll-mt-[72px]" data-testid="landing-treatments">
          <div className="absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-[hsl(var(--accent))]/[0.14] blur-3xl" aria-hidden="true" />
          <div className={cn(wide, "relative py-16 lg:py-28")}>
            <div className="grid gap-8 lg:grid-cols-2 lg:items-end">
              <div>
                <div className="eyebrow text-[hsl(var(--offwhite))]/50">Se forskjellen</div>
                <h2 className="display mt-3 text-[42px] sm:text-[56px] lg:text-[72px]">Tre behandlinger. Ett rom om gangen<span className="dot">.</span></h2>
              </div>
              <p className="max-w-[48ch] text-[17px] leading-[1.5] text-[hsl(var(--offwhite))]/70 sm:text-[19px] lg:pb-3">Motoren velger riktig nivå for hvert bilde. Et bra rom optimaliseres. Et møblert rom med potensial styles. Et tomt rom møbleres.</p>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-3 lg:mt-16">
              {BEHANDLINGER.map((b) => (
                <div key={b.kind} className="rounded-[20px] bg-white/[0.05] ring-1 ring-white/10 p-3 flex flex-col" data-testid={`landing-treatment-${b.kind}`}>
                  <Compare before={b.before} after={b.after} kind={b.kind} beforeLabel="Ekte bilde" sweep initial={b.kind === "optimize_only" ? 50 : 38} aspect="16 / 10" rounded="rounded-[14px]" className="bg-black/20" testid={`landing-treatment-compare-${b.kind}`}>
                    <span className="absolute bottom-3 left-3 rounded-[8px] bg-black/55 text-white px-2.5 py-1 text-[12px] font-medium backdrop-blur pointer-events-none">Dra i linjen</span>
                  </Compare>
                  <div className="p-3 sm:p-4 flex flex-col gap-4 flex-1">
                    <span className="font-heading text-[15px] text-[hsl(var(--offwhite))]/45 tabular-nums">{b.nr}</span>
                    <h3 className="font-heading text-[30px] leading-[1.02] tracking-[-0.02em]">{b.title}</h3>
                    <p className="text-[15px] leading-[1.55] text-[hsl(var(--offwhite))]/70">{b.text}</p>
                    <ul className="mt-auto space-y-2 border-t border-white/10 pt-4">{b.punkter.map((p) => <li key={p} className="flex items-start gap-2 text-[14.5px] text-[hsl(var(--offwhite))]/85"><Check className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--accent))]" strokeWidth={1.8} />{p}</li>)}</ul>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[12.5px] text-[hsl(var(--offwhite))]/45">Eksempelrom laget for å ligne typiske annonsebilder. «Etter» er MagicAd-motorens faktiske resultat, godkjent av den samme kontrollen som brukes på dine bilder.</p>
          </div>
        </section>

        {/* honest by design */}
        <section id="trygt" className="scroll-mt-[72px]" data-testid="landing-trust-section">
          <div className={cn(wide, "py-16 lg:py-28 grid gap-10 lg:grid-cols-2 lg:gap-16 items-center")}>
            <div>
              <div className="eyebrow">Trygt og ærlig</div>
              <h2 className="display mt-3 text-[42px] sm:text-[56px] lg:text-[64px]">Boligen slik den er. På sin beste dag<span className="dot">.</span></h2>
              <p className="mt-5 max-w-[42ch] text-[17px] leading-[1.5] text-dim">Forbrukertilsynet er tydelig: bildene skal vise boligen slik den er. MagicAd er laget rundt det – ikke på tross av det.</p>
              <ul className="mt-8 space-y-3.5">
                {TRYGT.map((t) => <li key={t} className="flex items-start gap-3 text-[15.5px] leading-[1.45]"><span className="mt-[3px] h-5 w-5 shrink-0 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--ink))] flex items-center justify-center"><Check className="h-3 w-3" strokeWidth={2.2} /></span>{t}</li>)}
              </ul>
            </div>
            <div>
              <Compare {...D("kjokken")} kind="soft_style" beforeLabel="Ekte bilde" sweep initial={45} rounded="rounded-[20px] sm:rounded-[24px]" className="card-shadow" testid="landing-demo-kitchen" />
              <div className="mt-3 rounded-[14px] bg-card ring-hair p-4 flex gap-3">
                <ShieldCheck className="h-5 w-5 shrink-0 text-[hsl(var(--accent-strong))]" />
                <div>
                  <div className="text-[14px] font-medium">To kontroller på hvert bilde</div>
                  <div className="mt-1 text-[13.5px] leading-[1.5] text-dim">Pikselvis sammenligning mot originalen, og en uavhengig modell som svarer på ett spørsmål: er dette fortsatt samme rom? Skap, vindu, benk og gulv er urørt – bare rotet er borte.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* faq */}
        <section id="faq" className="border-t hair scroll-mt-[72px]" data-testid="landing-faq">
          <div className={cn(wide, "py-16 lg:py-24 grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16")}>
            <div>
              <div className="eyebrow">Spørsmål og svar</div>
              <h2 className="display mt-3 text-[42px] sm:text-[52px]">Det folk lurer på<span className="dot">.</span></h2>
            </div>
            <div className="divide-y hair border-y hair">
              {FAQ.map(([q, a], i) => (
                <div key={q}>
                  <button type="button" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} className="w-full flex items-center justify-between gap-4 py-5 text-left text-[17px] font-medium" data-testid={`faq-${i}`}>
                    {q}<ChevronDown className={cn("h-4 w-4 shrink-0 transition-transform duration-300", open === i && "rotate-180")} strokeWidth={1.6} />
                  </button>
                  {open === i && <p className="pb-6 -mt-1 max-w-[62ch] text-[15px] leading-[1.55] text-dim">{a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* finale */}
        <section id="start" className="relative overflow-hidden bg-[hsl(var(--charcoal))] text-[hsl(var(--offwhite))]" data-testid="landing-finale">
          <div aria-hidden="true" className="absolute inset-0">
            <img src="/v4/bolig-oslo-2000.webp" srcSet="/v4/bolig-oslo-1200.webp 1200w, /v4/bolig-oslo-2000.webp 2000w" sizes="100vw" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ objectPosition: "50% 35%" }} />
          </div>
          <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,18,15,0.66) 0%, rgba(21,18,15,0.50) 45%, rgba(21,18,15,0.84) 100%)" }} />
          <div className={cn(wide, "relative pb-16 pt-16 sm:pb-24 sm:pt-24 lg:pb-32 lg:pt-36")}>
            <div className="max-w-[880px]">
              <h2 className="display text-[clamp(48px,6.2vw,104px)]" data-testid="landing-finale-title">Prøv med din egen annonse<span className="dot">.</span></h2>
              <p className="mt-6 max-w-[40ch] text-[18px] leading-[1.45] text-[hsl(var(--offwhite))]/78 sm:text-[21px]">Gratis å opprette konto. Første kjøring tar 1–2 minutter, og du ser før og etter på hvert bilde før du bestemmer noe.</p>
              <div className="relative z-20 mt-9 w-full sm:max-w-[640px]"><Handling onUrl={startUrl} onFiles={startFiles} dark testid="finale" /></div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t hair" data-testid="landing-footer">
        <div className={cn(wide, "flex flex-col gap-6 py-10 sm:flex-row sm:items-center sm:justify-between text-[14px] text-dim")}>
          <MagicAdLogo size="lg" />
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/logg-inn" className="hover:text-foreground">Logg inn</Link>
            <Link to="/registrer" className="hover:text-foreground">Opprett konto</Link>
            <a href="https://digihome.no" target="_blank" rel="noreferrer" className="hover:text-foreground">digihome.no</a>
            <Link to="/lab" className="hover:text-foreground" data-testid="landing-lab-link">For DigiHome-ansatte</Link>
          </div>
          <div>© {new Date().getFullYear()} DigiHome</div>
        </div>
      </footer>
    </div>
  );
}
